import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { useEffect } from "react";
import api from "@/lib/api";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Products from "@/pages/Products";
import GoodsReceipt from "@/pages/GoodsReceipt";
import POS from "@/pages/POS";
import VanSales from "@/pages/VanSales";
import SalesHistory from "@/pages/SalesHistory";
import Opportunities from "@/pages/Opportunities";
import Users from "@/pages/Users";
import CashCenter from "@/pages/CashCenter";
import Personnel from "@/pages/Personnel";
import Locations from "@/pages/Locations";
import Inventory from "@/pages/Inventory";
import Count from "@/pages/Count";
import Transfers from "@/pages/Transfers";
import Purchasing from "@/pages/Purchasing";
import Suppliers from "@/pages/Suppliers";
import Roles from "@/pages/Roles";
import Categories from "@/pages/Categories";
import Fx from "@/pages/Fx";
import Reports from "@/pages/Reports";
import Customers from "@/pages/Customers";
import Quotes from "@/pages/Quotes";
import AiAssistant from "@/pages/AiAssistant";
import Integrations from "@/pages/Integrations";
import Approvals from "@/pages/Approvals";
import Chat from "@/pages/Chat";
import Logistics from "@/pages/Logistics";
import EDocuments from "@/pages/EDocuments";
import Accounting from "@/pages/Accounting";
import CustomerDisplay from "@/pages/CustomerDisplay";
import Campaigns from "@/pages/Campaigns";
import Settings from "@/pages/Settings";
import DocumentArchive from "@/pages/DocumentArchive";
import Security from "@/pages/Security";
import Scoring from "@/pages/Scoring";
import Waste from "@/pages/Waste";
import Surveys from "@/pages/Surveys";
import SurveyPublic from "@/pages/SurveyPublic";
import ReceiptVerify from "@/pages/ReceiptVerify";
import WholesaleAudit from "@/pages/WholesaleAudit";
import FeedbackPublic from "@/pages/FeedbackPublic";
import Feedback from "@/pages/Feedback";
import Esl from "@/pages/Esl";
import PriceHistory from "@/pages/PriceHistory";
import Pricing from "@/pages/Pricing";
import KdvReview from "@/pages/KdvReview";
import CatalogHealth from "@/pages/CatalogHealth";
import ScheduledTasks from "@/pages/ScheduledTasks";
import PriceManagement from "@/pages/PriceManagement";
import SecurityLedger from "@/pages/SecurityLedger";
import AccessAudit from "@/pages/AccessAudit";
import RiskRadar from "@/pages/RiskRadar";
import MyPayslips from "@/pages/MyPayslips";
import FieldVisits from "@/pages/FieldVisits";
import PlatformManagement from "@/components/PlatformManagement";
import { WorkGuardProvider } from "@/context/WorkGuard";
import { ActiveLocationProvider } from "@/context/ActiveLocationContext";
import { StepUpProvider } from "@/context/StepUp";
import { Loader2 } from "lucide-react";

// Persistent shell: mounts <Layout> once so sidebar scroll/state survives navigation.
function ProtectedLayout() {
  const { user, booting } = useAuth();
  useEffect(() => { if (user) api.post("/market/ensure-fresh").catch(() => {}); }, [user]);
  if (booting || user === null) {
    return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand-green" /></div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  return <Layout><Outlet /></Layout>;
}

// Per-route permission guard (Layout already mounted by ProtectedLayout).
function Guard({ children, perm, anyPerm, owner }) {
  const { can, user } = useAuth();
  if (owner && !user?.is_owner) return <Navigate to="/" replace />;
  if (perm && !can(perm)) return <Navigate to="/" replace />;
  if (anyPerm && !anyPerm.some((p) => can(p))) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const { user, booting } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={(!booting && user) ? <Navigate to="/" replace /> : <Login />} />
      {/* Public bare routes (no auth, no layout) */}
      <Route path="/anket" element={<SurveyPublic />} />
      <Route path="/dogrula" element={<ReceiptVerify />} />
      <Route path="/oneri" element={<FeedbackPublic />} />
      <Route path="/musteri-ekran" element={<CustomerDisplay />} />

      {/* Authenticated app — single persistent Layout via <Outlet /> */}
      <Route element={<ProtectedLayout />}>
        <Route path="/" element={<Guard perm="dashboard.read"><Dashboard /></Guard>} />
        <Route path="/panel" element={<Navigate to="/" replace />} />
        <Route path="/urunler" element={<Guard perm="price.read"><Products /></Guard>} />
        <Route path="/fiyat-yonetimi" element={<Guard perm="price.read"><PriceManagement /></Guard>} />
        <Route path="/toplu-fiyat" element={<Guard perm="price.write"><Pricing /></Guard>} />
        <Route path="/kdv-dogrulama" element={<Guard perm="price.read"><KdvReview /></Guard>} />
        <Route path="/katalog-sagligi" element={<Guard perm="price.read"><CatalogHealth /></Guard>} />
        <Route path="/fiyat-gecmisi" element={<Guard perm="price.write"><PriceHistory /></Guard>} />
        <Route path="/raf-etiketleri" element={<Guard perm="esl.read"><Esl /></Guard>} />
        <Route path="/firsatlar" element={<Guard perm="purchase.read"><Opportunities /></Guard>} />
        <Route path="/mal-kabul" element={<Guard perm="receipt.read"><GoodsReceipt /></Guard>} />
        <Route path="/stok" element={<Guard perm="stock.read"><Inventory /></Guard>} />
        <Route path="/sayim" element={<Guard perm="stock.read"><Count /></Guard>} />
        <Route path="/transferler" element={<Guard perm="transfer.read"><Transfers /></Guard>} />
        <Route path="/satin-alma" element={<Guard perm="transfer.read"><Purchasing /></Guard>} />
        <Route path="/tedarikciler" element={<Guard perm="supplier.read"><Suppliers /></Guard>} />
        <Route path="/kasa" element={<Guard perm="sale.create"><POS /></Guard>} />
        <Route path="/rota-satis" element={<Guard anyPerm={["van.sale", "sale.create"]}><VanSales /></Guard>} />
        <Route path="/saha-ziyaret" element={<Guard anyPerm={["visit.check", "visit.read"]}><FieldVisits /></Guard>} />
        <Route path="/satislar" element={<Guard perm="sale.read"><SalesHistory /></Guard>} />
        <Route path="/vardiyalar" element={<Navigate to="/kasalar?tab=z" replace />} />
        <Route path="/raporlar" element={<Guard perm="report.read"><Reports /></Guard>} />
        <Route path="/musteriler" element={<Guard perm="customer.read"><Customers /></Guard>} />
        <Route path="/teklifler" element={<Guard perm="customer.read"><Quotes /></Guard>} />
        <Route path="/kampanyalar" element={<Guard anyPerm={["price.campaign", "price.write"]}><Campaigns /></Guard>} />
        <Route path="/onaylar" element={<Guard perm="approve.chain"><Approvals /></Guard>} />
        <Route path="/gecici-fiyatlar" element={<Navigate to="/fiyat-yonetimi" replace />} />
        <Route path="/halk-gunu" element={<Navigate to="/fiyat-yonetimi?tab=zamanli" replace />} />
        <Route path="/fiyat-bekleyenler" element={<Navigate to="/urunler?filter=unpriced" replace />} />
        <Route path="/mesajlar" element={<Guard perm="dashboard.read"><Chat /></Guard>} />
        <Route path="/ai" element={<Guard perm="ai.use"><AiAssistant /></Guard>} />
        <Route path="/entegrasyonlar" element={<Guard perm="role.manage"><Integrations /></Guard>} />
        <Route path="/kategoriler" element={<Guard perm="category.manage"><Categories /></Guard>} />
        <Route path="/kurlar" element={<Guard perm="fx.manage"><Fx /></Guard>} />
        <Route path="/roller" element={<Guard perm="role.manage"><Roles /></Guard>} />
        <Route path="/kullanicilar" element={<Guard perm="user.view"><Users /></Guard>} />
        <Route path="/kasalar" element={<Guard anyPerm={["report.read", "register.manage", "user.assign", "location.manage", "user.manage"]}><CashCenter /></Guard>} />
        <Route path="/lokasyonlar" element={<Guard perm="location.manage"><Locations /></Guard>} />
        <Route path="/belge-arsivi" element={<Guard perm="config.manage"><DocumentArchive /></Guard>} />
        <Route path="/guvenlik" element={<Guard perm="user.manage"><Security /></Guard>} />
        <Route path="/guvenlik-gozu" element={<Guard perm="approve.admin"><SecurityLedger /></Guard>} />
        <Route path="/erisim-izi" element={<Guard perm="approve.admin"><AccessAudit /></Guard>} />
        <Route path="/risk-radar" element={<Guard anyPerm={["approve.admin", "report.read", "accounting.read"]}><RiskRadar /></Guard>} />
        <Route path="/zamanlanmis-gorevler" element={<Guard perm="user.manage"><ScheduledTasks /></Guard>} />
        <Route path="/lojistik" element={<Guard perm="logistics.read"><Logistics /></Guard>} />
        <Route path="/muhasebe" element={<Guard perm="accounting.read"><Accounting /></Guard>} />
        <Route path="/personel" element={<Guard perm="accounting.payroll"><Personnel /></Guard>} />
        <Route path="/e-belge" element={<Guard perm="edoc.read"><EDocuments /></Guard>} />
        <Route path="/bordrom" element={<MyPayslips />} />
        <Route path="/cihazlar" element={<Navigate to="/ayarlar" replace />} />
        <Route path="/ayarlar" element={<Guard perm="config.manage"><Settings /></Guard>} />
        <Route path="/platform" element={<Guard owner><PlatformManagement /></Guard>} />
        <Route path="/fire" element={<Guard perm="stock.adjust"><Waste /></Guard>} />
        <Route path="/puan-odul" element={<Guard perm="score.read"><Scoring /></Guard>} />
        <Route path="/anketler" element={<Guard anyPerm={["survey.read", "report.read"]}><Surveys /></Guard>} />
        <Route path="/toptan-denetim" element={<Guard perm="report.read"><WholesaleAudit /></Guard>} />
        <Route path="/oneriler" element={<Navigate to="/anketler?tab=feedback" replace />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <WorkGuardProvider>
        <ActiveLocationProvider>
          <BrowserRouter>
            <StepUpProvider>
              <AppRoutes />
            </StepUpProvider>
            <Toaster position="top-right" richColors offset={72} />
          </BrowserRouter>
        </ActiveLocationProvider>
      </WorkGuardProvider>
    </AuthProvider>
  );
}
